import { useCallback, useEffect, useLayoutEffect, useState } from 'react';

import { Capability } from '@/constants/capabilities';
import { MANDATORY_ROLE } from '@/constants/roles';
import { ROUTES } from '@/constants/routes';
import { useAuthStore } from '@/features/auth/stores/useAuthStore';
import { useMarketStore } from '@/features/markets/stores/useMarketStore';
import { UserMarket } from '@/features/users/types/user';
import { useNavigate } from '@/hooks/useNavigate';

export interface UseAccessPermissions {
    isSuperAdmin: boolean;
    hasAccess: boolean;
    checkAccess: (capabilityName: Capability) => boolean;
    checkAccessByMarketId: (capabilityName: Capability, marketId: string) => boolean;
}

export function useAccessPermissions(initialCapability?: Capability | Capability[]): UseAccessPermissions {
    const { navigate } = useNavigate();
    const { authUser } = useAuthStore();
    const { market } = useMarketStore();

    const initialUserMarket = authUser?.usersMarkets.find(({ market }) => market.id === market?.id);

    const [hasAccess, setHasAccess] = useState(true);
    const [currentUserMarket, setCurrentUserMarket] = useState<UserMarket | undefined>(initialUserMarket);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    useLayoutEffect(() => {
        if (!authUser?.usersMarkets || !market?.id) {
            setCurrentUserMarket(undefined);
            return;
        }
        const foundMarket = authUser.usersMarkets.find(({ market }) => market.id === market?.id);

        setCurrentUserMarket(foundMarket);
        setIsSuperAdmin(!!foundMarket?.roles.find(({ id }) => id === MANDATORY_ROLE.SUPER_ADMIN));
    }, [market?.id, authUser?.usersMarkets]);

    // NOTE: Checks the access looking for the requested capability into the role capabilities from de user
    const hasCapabilityByRole = useCallback(
        (capabilityName: Capability) =>
            !!currentUserMarket?.roles.find(({ capabilities }) => capabilities.find(({ id }) => id === capabilityName)),
        [currentUserMarket],
    );

    const checkAccess = useCallback(
        (capabilityName: Capability) => {
            if (!authUser) return false;
            if (!market?.id) return false;

            const foundMarket = authUser.usersMarkets.find(({ market }) => market.id === market?.id);
            const superAdmin = !!foundMarket?.roles.find(({ id }) => id === MANDATORY_ROLE.SUPER_ADMIN);

            return superAdmin ? true : hasCapabilityByRole(capabilityName);
        },
        [authUser, market?.id, hasCapabilityByRole],
    );

    const hasCapabilityForMarket = useCallback(
        (capabilityName: Capability, marketId: string) => {
            const market = authUser?.usersMarkets?.find(({ market }) => market.id === marketId);

            return market
                ? market.roles.some(
                      ({ id, capabilities }) =>
                          id === MANDATORY_ROLE.SUPER_ADMIN || capabilities.some(({ id }) => id === capabilityName),
                  )
                : false;
        },
        [authUser?.usersMarkets],
    );

    const checkAccessByMarketId = useCallback(
        (capabilityName: Capability, marketId: string) =>
            authUser ? hasCapabilityForMarket(capabilityName, marketId) : false,
        [authUser, hasCapabilityForMarket],
    );

    // NOTE: Check currentUser role, if it is superAdmin has access always, if not, check role capabilities
    useEffect(() => {
        if (!initialCapability) return;
        if (Array.isArray(initialCapability) && initialCapability.length === 0) return;

        const hasInitialAccess = Array.isArray(initialCapability)
            ? initialCapability.some((capacity) => checkAccess(capacity))
            : checkAccess(initialCapability);

        setHasAccess(hasInitialAccess);
    }, [initialCapability, checkAccess]);

    // NOTE: Redirect to index when currentUser has no permissions
    useEffect(() => {
        if (!hasAccess) {
            navigate(ROUTES.root.route);
        }
    }, [hasAccess, navigate]);

    return { isSuperAdmin, hasAccess, checkAccess, checkAccessByMarketId };
}
